import React, { useEffect, useRef, useState } from "react"
import { useRecoilValue } from "recoil"

import { IQuote } from "@/types/quotes.types"

import { VehicleType } from "@/types/vehicle.types"
import KosmoLoadingSkeleton from "@/UI/Skeletons/KosmoLoadingSkeleton"
import { AnimatePresence, motion } from "framer-motion"
import { useInterval, useMediaQuery } from "usehooks-ts"
import { Tabs } from "antd"

import { isEmpty, uniqBy } from "lodash"
import TabPane from "antd/lib/tabs/TabPane"
import OwnDriverCard from "@/UI/DataDisplay/OwnDriverCard/OwnDriverCard"
import { useDrivers } from "@/hooks/useDrivers"
import {
  is3PLsEnabledSelector,
  isKosmoNetworkSelector,
  isPayingUserSelector,
} from "@/atoms/userAtom"
import VehicleTypeSelector from "@/modules/shared/components/VehicleTypeSelector"
import DeliveryOptionsList from "@/modules/shared/components/DeliveryOptionsList"

import { Loader } from "@/UI"
import KosmoNetworkDeliveryOptions from "@/modules/shared/components/KosmoNetworkDeliveryOptions.container"
import { isStandardDelivery } from "@/utils/delivery.utils"
import { AxiosResponse } from "axios"
import { useTranslation } from "react-i18next"
import { sortDrivers } from "@/utils/drivers"
import { faGear } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

interface SelectDriverOrCourierTabsProps {
  onSelectCallback?: (quoteId: string | undefined) => void | Promise<void>
  // If time, quote can maybe be handled here
  selectedQuote: IQuote | null | undefined
  setSelectedQuote: (selectedQuote: IQuote | null) => void
  isSelectQuoteLoading: boolean
  handleSelectDriver: (driverId: string, teamId?: number) => Promise<void> | void
  getCouriersQuotes: () => Promise<any>
  assignedTeamId?: number
  orderSchedule?: number
  asyncPollingHandler?: () => Promise<
    AxiosResponse<
      {
        quoteBatchId: string
        quotes: IQuote[]
        status: "fetching" | "done"
      },
      any
    >
  >
  shouldRefreshQuotes?: boolean
  selectedDriverId?: string
  compact?: boolean
  showOwnFleet: boolean
  show3pls?: boolean
}

const SelectDriverOrCouriersTabs: React.FC<SelectDriverOrCourierTabsProps> = ({
  onSelectCallback,
  selectedQuote,
  setSelectedQuote,
  isSelectQuoteLoading,
  handleSelectDriver,
  assignedTeamId,
  getCouriersQuotes,
  asyncPollingHandler,
  orderSchedule,
  shouldRefreshQuotes,
  selectedDriverId,
  compact,
  showOwnFleet = true,
  show3pls = true,
}) => {
  const { t } = useTranslation()
  const isDesktop = useMediaQuery("(min-width: 964px)")
  const isKosmoNetwork = useRecoilValue(isKosmoNetworkSelector)
  const isPayingUser = useRecoilValue(isPayingUserSelector)
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleType | null>()
  const is3PlsEnabled = useRecoilValue(is3PLsEnabledSelector)

  const [quotes, setQuotes] = useState<IQuote[]>([])
  const [isUpdatingDriver, setIsUpdatingDriver] = useState<boolean>(false)
  const [isQuotesLoading, setIsQuotesLoading] = useState(false)
  const [isFetchingAsyncQuotes, setIsFetchingAsyncQuotes] = useState<boolean>(false)

  const containerRef = useRef<HTMLDivElement>(null)

  const { drivers, isLoading: isLoadingDrivers } = useDrivers()

  const vehicleFilters = [
    VehicleType.MOTORCYCLE,
    VehicleType.CAR,
    VehicleType.VAN,
    VehicleType.LORRY,
  ]

  const updateDraftOwnDriver = async (driverId: string, teamId?: number) => {
    setIsUpdatingDriver(true)
    try {
      await handleSelectDriver(driverId, teamId)
      setIsUpdatingDriver(false)
      if (onSelectCallback) {
        await onSelectCallback(undefined)
      }
    } catch (error) {
      console.log(error)
      setIsUpdatingDriver(false)
    }
  }

  const handleSelectCourierQuote = async (selectedQuote: IQuote | null) => {
    setSelectedQuote(selectedQuote)
    if (onSelectCallback) {
      await onSelectCallback(selectedQuote?.id)
    }
  }

  // Handle async polling if isAsync (draft)
  const EXECUTION_INTERVAL = 0.6
  useInterval(async () => {
    const needToFetch = asyncPollingHandler && isFetchingAsyncQuotes
    if (needToFetch) {
      const { data } = await asyncPollingHandler()
      const { quotes: asyncQuotes, status } = data
      const sortedQuotes = asyncQuotes?.sort((a, b) => (a?.price < b?.price ? -1 : 1))

      if (quotes?.length !== sortedQuotes?.length) {
        setQuotes([...sortedQuotes])
      }

      if (status === "done") {
        setIsFetchingAsyncQuotes(false)
      }
    }
  }, 1000 * EXECUTION_INTERVAL)

  const handleSelectVehicle = (vehicleType: VehicleType) => {
    if (selectedVehicle && selectedVehicle === vehicleType) {
      setSelectedVehicle(null)
    } else {
      setSelectedVehicle(vehicleType)
    }
  }

  const displayedDrivers = sortDrivers(
    drivers.filter((driver) => {
      const selectedVehicleFilter = selectedVehicle ? driver.vehicleType === selectedVehicle : true
      const teamFilter = assignedTeamId ? driver.teamIDs?.includes(assignedTeamId) : true
      return selectedVehicleFilter && teamFilter
    })
  )

  const handleFetchQuotes = async () => {
    try {
      if (!show3pls) {
        return
      }

      setIsQuotesLoading(true)
      const { data } = await getCouriersQuotes()
      if (asyncPollingHandler) {
        setIsFetchingAsyncQuotes(true)
      } else if (data?.quotes) {
        const sortedQuotes = data?.quotes?.sort((a: IQuote, b: IQuote) =>
          a?.price < b?.price ? -1 : 1
        )
        setQuotes(sortedQuotes)
      }
    } finally {
      setIsQuotesLoading(false)
    }
  }

  useEffect(() => {
    if ((!isLoadingDrivers && isEmpty(drivers) && is3PlsEnabled) || !showOwnFleet) {
      handleFetchQuotes()
    }
  }, [isLoadingDrivers])

  const sortedFilteredQuotes = quotes
    .filter((quote) => {
      const isErrorQuote = quote.error
      const isVehicleFilter =
        selectedVehicle && !isStandardDelivery(quote.deliveryType)
          ? selectedVehicle === quote.vehicleType
          : true
      // Get schedule from upper component
      const isScheduledStandard = isStandardDelivery(quote.deliveryType) && orderSchedule
      return !isErrorQuote && isVehicleFilter && !isScheduledStandard
    })
    .sort((a, b) => a?.priceBreakdown?.total - b?.priceBreakdown?.total)
  const uniqQuotesByDeliveryType = uniqBy(sortedFilteredQuotes, (quote) => quote.deliveryType)

  return (
    <div className="px-2 pt-4 h-full" onClick={(e) => e.stopPropagation()} ref={containerRef}>
      <AnimatePresence>
        <motion.div
          id="delivery-options"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ minWidth: "250px" }}
        >
          <div className="flex flex-col w-full overflow-hidden">
            <div className="flex items-center justify-between gap-x-2">
              <p className={`pb-2 text-gray-500 font-light`}>
                {t("create-order.DeliveryOptions.selectedPreferredQuote")}
              </p>
              <a
                className="text-sm flex items-center pb-2"
                onClick={() => window.open("/profile/?p=couriers", "_blank")}
                hidden={!is3PlsEnabled}
              >
                <FontAwesomeIcon icon={faGear} />
                {!compact && <p className="ml-1">{t("common.configure")}</p>}
              </a>
            </div>
            <div className={`flex flex-col gap-y-2`}>
              {vehicleFilters.length > 1 && (
                <VehicleTypeSelector
                  vehicles={vehicleFilters}
                  selectedVehicleType={selectedVehicle}
                  onClick={handleSelectVehicle}
                  displayType="compact"
                />
              )}
              {isLoadingDrivers ? (
                <KosmoLoadingSkeleton type={"deliveryOptionsCompact"} />
              ) : (
                <Tabs
                  className={`${isDesktop ? "" : "min-w-[150px]"}`}
                  defaultActiveKey={`${isEmpty(drivers) ? "couriers" : "ownDrivers"}`}
                  destroyInactiveTabPane
                  onChange={async (activeKey) => {
                    if (activeKey === "couriers" && (isEmpty(quotes) || shouldRefreshQuotes)) {
                      await handleFetchQuotes()
                    }
                  }}
                >
                  {!isEmpty(drivers) && showOwnFleet && (
                    <TabPane
                      className={`overflow-x-hidden relative ${isEmpty(drivers) ? "hidden" : ""} ${
                        isUpdatingDriver ? "opacity-50" : ""
                      }`}
                      tab={<p className="w-full text-sm !font-normal">{t("common.ownDrivers")}</p>}
                      key={"ownDrivers"}
                    >
                      <div
                        className="flex flex-col gap-y-2 w-full pr-2"
                        style={{ maxHeight: compact ? "300px" : undefined }}
                      >
                        {isEmpty(displayedDrivers) ? (
                          <div className="flex w-full justify-center mt-2">
                            <p className="font-semibold">
                              {t("common.DeliveryOptionList.noDriversMatch")}
                            </p>
                          </div>
                        ) : (
                          displayedDrivers.map((driver, index) => (
                            <OwnDriverCard
                              key={index}
                              index={index}
                              driver={driver}
                              isSelected={driver?.id === selectedDriverId}
                              onClick={() =>
                                updateDraftOwnDriver(
                                  driver.id,
                                  assignedTeamId || driver?.teamIDs?.[0]
                                )
                              }
                            />
                          ))
                        )}
                      </div>
                    </TabPane>
                  )}
                  {is3PlsEnabled && show3pls && (
                    <>
                      <TabPane
                        tab={
                          <p className="w-full text-sm !font-normal">
                            {isKosmoNetwork
                              ? "Kosmo Network"
                              : t("common.SelectDriverOrCourierTabs.couriers")}
                          </p>
                        }
                        key={"couriers"}
                      >
                        {isQuotesLoading || (isFetchingAsyncQuotes && quotes?.length === 0) ? (
                          <KosmoLoadingSkeleton
                            type={compact ? "deliveryOptionsCompact" : "deliveryOptions"}
                          />
                        ) : (
                          <>
                            {isKosmoNetwork ? (
                              <KosmoNetworkDeliveryOptions
                                quotes={uniqQuotesByDeliveryType}
                                selectedQuote={selectedQuote}
                                onSelect={handleSelectCourierQuote}
                                displayType={compact ? "compact" : "full"}
                                selectionDisabled={!isPayingUser}
                              />
                            ) : (
                              <DeliveryOptionsList
                                quotes={quotes.filter((quote) => {
                                  const selectedVehicleFilter = selectedVehicle
                                    ? quote.vehicleType === selectedVehicle
                                    : true
                                  return selectedVehicleFilter
                                })}
                                selectedQuote={selectedQuote}
                                onSelect={handleSelectCourierQuote}
                                compact={compact}
                                selectionDisabled={!isPayingUser}
                              />
                            )}
                          </>
                        )}
                      </TabPane>
                    </>
                  )}
                </Tabs>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
      {isSelectQuoteLoading && (
        <div className="absolute top-0 left-0 w-full h-full z-10 bg-[rgba(255,255,255,0.5)] flex items-center justify-center">
          <Loader fontSize={28} />
        </div>
      )}
    </div>
  )
}

export default SelectDriverOrCouriersTabs
